import {$taxesAndTotal} from '..';
import {AllowanceCharge, TaxesAndTotal} from '../../../types/invoices';

function calcualteVatAndTotalAmountByCategory(allowanceChargeData: AllowanceCharge[]) {
    return allowanceChargeData.reduce(
        (t, i) => {
            const {isCharge, totalAmount, vatCategory, vat} = i;
            if (!vatCategory) {
                return t;
            }
            const totalMainKey = `${(vatCategory.value as string) as keyof typeof t} :: ${Number(
                vat,
            )}`;

            if (totalMainKey in t) {
                return {
                    ...t,
                    [totalMainKey]: {
                        ...t[totalMainKey],
                        totalAmount:
                            isCharge === 'charge'
                                ? t[totalMainKey].totalAmount + Number(totalAmount)
                                : t[totalMainKey].totalAmount - Number(totalAmount),
                    },
                };
            }

            const initalValue = 0;
            return {
                ...t,
                [totalMainKey]: {
                    vat: Number(vat),
                    totalAmount:
                        isCharge === 'charge'
                            ? initalValue + Number(totalAmount)
                            : initalValue - Number(totalAmount),
                },
            };
        },
        {} as Record<
            string,
            {
                vat: number;
                totalAmount: number;
            }
        >,
    );
}

const calcTaxTotal = (data: TaxesAndTotal['calculationsByTaxCategory']) =>
    Object.values(data)
        .reduce((t, i) => Number(i.taxAmountDisplay) + t, 0)
        .toFixed(2);

function setAllownaceAndCharges(updatedAllowanceCharge: AllowanceCharge[]) {
    const initialObj = {
        allowanceTotalAmountDisplay: 0,
        chargeTotalAmountDisplay: 0,
    };

    return updatedAllowanceCharge.reduce((t, i) => {
        const isCharge = i.isCharge === 'charge';
        return {
            ...t,
            ...(isCharge
                ? {chargeTotalAmountDisplay: t.chargeTotalAmountDisplay + Number(i.totalAmount)}
                : {
                      allowanceTotalAmountDisplay:
                          t.allowanceTotalAmountDisplay + Number(i.totalAmount),
                  }),
        };
    }, initialObj);
}

const setTaxExclusiveAmountDisplay = ({
    data,
    allowanceTotalAmount,
    chargeTotalAmount,
}: {
    data: TaxesAndTotal;
    allowanceTotalAmount: number;
    chargeTotalAmount: number;
}) => {
    const {lineExtensionAmount} = data;
    return Number(lineExtensionAmount) - allowanceTotalAmount + chargeTotalAmount;
};

export function calcUpdatingTaxesAndTotalAllowanceCharge(val: AllowanceCharge | number) {
    const taxesAndTotal = $taxesAndTotal.getState();
    const {
        calculationsByTaxCategory,
        allowanceCharge,
        prepaidAmount,
        payableRoundingAmount,
    } = taxesAndTotal.data;
    const currentAllowanceCharge: AllowanceCharge[] = allowanceCharge ?? [];

    if (typeof val === 'number') {
        if (!allowanceCharge || val < 0 || val >= allowanceCharge.length) {
            return {
                data: taxesAndTotal.data,
                allowToSend: false,
            };
        }
    }

    const updatedAllowanceCharge =
        typeof val === 'number'
            ? currentAllowanceCharge.filter((_, index) => index !== val)
            : [...currentAllowanceCharge, val];

    const calcualtedVATAndTotal = calcualteVatAndTotalAmountByCategory(updatedAllowanceCharge);
    const updatedCalculationsByTaxCategory = calculationsByTaxCategory?.map(
        (item: TaxesAndTotal['calculationsByTaxCategory'][number]) => {
            const vatTotals = calcualtedVATAndTotal[item.taxCategory];
            if (!vatTotals) {
                return {
                    ...item,
                    taxableAmountDisplay: Number(item.taxableAmount).toFixed(2),
                    taxAmountDisplay: Number(item.taxAmount).toFixed(2),
                };
            }

            const calcTaxAmount = Number(item.taxableAmount) + vatTotals.totalAmount;
            const calcTaxAmountDisplay = (calcTaxAmount / 100) * Number(vatTotals.vat);

            return {
                ...item,
                taxableAmountDisplay: calcTaxAmount.toFixed(2),
                taxAmountDisplay: calcTaxAmountDisplay.toFixed(2),
            };
        },
    );

    const taxTotal = calcTaxTotal(updatedCalculationsByTaxCategory);

    const allowanceAndCharges = setAllownaceAndCharges(updatedAllowanceCharge);
    const {chargeTotalAmountDisplay, allowanceTotalAmountDisplay} = allowanceAndCharges;

    const getTaxExclusiveAmountDisplay = setTaxExclusiveAmountDisplay({
        data: taxesAndTotal.data,
        allowanceTotalAmount: allowanceTotalAmountDisplay,
        chargeTotalAmount: chargeTotalAmountDisplay,
    });
    const taxExclusiveAmountDisplay = getTaxExclusiveAmountDisplay.toFixed(2);
    const getTaxInclusiveAmountDisplay = getTaxExclusiveAmountDisplay + Number(taxTotal);
    const taxInclusiveAmountDisplay = getTaxInclusiveAmountDisplay.toFixed(2);
    const getPayableAmount =
        Number(taxInclusiveAmountDisplay) - Number(prepaidAmount) + Number(payableRoundingAmount);
    const payableAmountDisplay = getPayableAmount.toFixed(2);

    return {
        data: {
            ...taxesAndTotal.data,
            allowanceCharge: updatedAllowanceCharge,
            ...(updatedCalculationsByTaxCategory
                ? {calculationsByTaxCategory: updatedCalculationsByTaxCategory}
                : {}),
            taxTotal,
            allowanceTotalAmountDisplay: allowanceTotalAmountDisplay.toFixed(2),
            chargeTotalAmountDisplay: chargeTotalAmountDisplay.toFixed(2),
            taxExclusiveAmountDisplay,
            taxInclusiveAmountDisplay,
            payableAmountDisplay,
        },
        allowToSend: false,
    };
}
